import type { DropdownOption } from "../../redistribution/models/DropdownOption.ts";
import type { IntermediateOrderSkuRow } from "../../redistribution/models/IntermediateOrderSkuRow.ts";
import { buildIntermediateOrderRows } from "../intermediateOrders/intermediateOrderRows.ts";
import type {
  AssociatedIntermediateOrder,
  BulkOrderCreationRow,
  BulkOrderLookupResult,
} from "./bulkOrderCreationMock.ts";

function normalizeOrderId(value: string): string {
  return value.replaceAll(/\s+/g, "").toUpperCase();
}

export function matchesIntermediateOrder(
  row: IntermediateOrderSkuRow,
  intermediateOrderId: string
): boolean {
  return (
    normalizeOrderId(row.intermediatePoNumber) ===
    normalizeOrderId(intermediateOrderId)
  );
}

/**
 * Maps the flat SKU-level rows returned by the live
 * `/api/PurchaseOrder/searchIntermediateOrders` search (shared with the
 * header search bar and the Bulk Order Lookup search-panel tab) into the
 * Bulk Order Creation view/modal display model.
 *
 * The search endpoint returns every IO of the parent bulk order even when
 * searched by an IO number, so `intermediateOrderId` narrows the SKU rows to
 * that IO while the associated IO list keeps all siblings for navigation.
 */
export function mapIntermediateOrderRowsToLookupResult(
  skuRows: IntermediateOrderSkuRow[],
  warehouseOptions: DropdownOption[] = [],
  intermediateOrderId?: string
): BulkOrderLookupResult {
  const selectedRows = intermediateOrderId
    ? skuRows.filter((row) =>
        matchesIntermediateOrder(row, intermediateOrderId)
      )
    : skuRows;
  // Editors are isolated by division; prices must agree within each editor.
  const pricesBySku = new Map<string, number | null>();
  for (const row of selectedRows) {
    const sku = `${row.division?.trim().toUpperCase() ?? ""}:${row.skuNumber.trim().toUpperCase()}`;
    const price = row.firstCostUsd ?? null;
    if (pricesBySku.has(sku) && pricesBySku.get(sku) !== price) {
      throw new Error(
        `FirstCost differs across source rows for SKU ${row.skuNumber}. Look up an individual Intermediate Order to price Suggested POs.`
      );
    }
    pricesBySku.set(sku, price);
  }
  const rows: BulkOrderCreationRow[] = selectedRows.map((row) => ({
    ...(row.division ? { division: row.division.trim().toUpperCase() } : {}),
    sku: row.skuNumber,
    unitPriceUsd: row.firstCostUsd ?? null,
    description: row.productDescription,
    ...(row.collection.trim() ? { theme: row.collection.trim() } : {}),
    ...(row.color.trim() ? { color: row.color.trim() } : {}),
    // Mirrors the API's SkuNumber.Substring(3, 4) Family derivation.
    ...(row.skuNumber.length >= 7 ? { family: row.skuNumber.slice(3, 7) } : {}),
    ...(Number.isFinite(row.currentStock)
      ? { currentStock: row.currentStock }
      : {}),
    ...(Number.isFinite(row.roS30) ? { roS30: row.roS30 } : {}),
    ...(Number.isFinite(row.roS60) ? { roS60: row.roS60 } : {}),
    ...(Number.isFinite(row.roS90) ? { roS90: row.roS90 } : {}),
    ...(row.pieceCount === undefined ? {} : { pieceCount: row.pieceCount }),
    shipDate: row.shipDateRangeStart || row.shipDateRangeEnd || "",
    requestedUnits: row.totalUnits,
    shipToWarehouse: row.warehouseName,
    warehouseNumber: row.warehouseNumber,
    status: row.distributionStatus,
    cubicFeet: row.totalCubicFeet,
  }));

  const associatedIntermediateOrders: AssociatedIntermediateOrder[] =
    buildIntermediateOrderRows(skuRows, warehouseOptions).map((order) => ({
      id: order.intermediatePoNumber,
      warehouse: order.warehouseDisplay,
      unitTotal: order.totalUnits,
    }));

  return { rows, associatedIntermediateOrders };
}
